import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from 'react-hook-form';

import { Accordion } from '@radix-ui/react-accordion';
import {
  BoxIcon,
  Building2,
  CircleDollarSignIcon,
  TruckIcon,
  WarehouseIcon,
  Weight,
} from 'lucide-react';

import { Button } from 'components/Button';
import LoadContextProvider from 'components/LoadContextProvider';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useGuardedCreateLoad } from 'hooks/useGuardedCreateLoad';
import { useServiceFeatures } from 'hooks/useServiceContext';
import useTMSContext from 'hooks/useTMSContext';
import { useToast } from 'hooks/useToaster';
import {
  TMSTrailerNumber,
  TMSTruckNumber,
  getTrailerNumbers,
  getTruckNumbers,
} from 'lib/api/getTruckTrailerNumbers';
import { updateTMS } from 'lib/api/updateTMS';
import { FormStorageService } from 'lib/services/FormStorage/service';
import { LoadSectionAccordionItem } from 'pages/LoadView/LoadInformation/Components';
import LoadBuildingAccordionControls from 'pages/QuoteView/LoadBuilding/components/LoadBuildingAccordionControls';
import { useLoadBuildingAccordionTabs } from 'pages/QuoteView/LoadBuilding/hooks/useLoadBuildingAccordionTabs';
import { suggestedTransportTypeOrEmpty } from 'pages/QuoteView/LoadBuilding/utils/suggestedEquipment';
import { ExternalLinks } from 'types/ExternalLinks';
import {
  Load,
  NormalizedLoad,
  Unit,
  createInitLoad,
  createInitRateData,
  createInitSpecs,
  normalizeLoad,
} from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';
import { TMSOperation } from 'types/api/UpdateTMS';
import ButtonText from 'types/enums/ButtonText';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { TMS } from 'types/enums/Integrations';
import {
  GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';
import { SuggestedLoad } from 'types/suggestions/LoadBuildingSuggestions';
import {
  acceptLoadViewSuggestion,
  useApplyLoadViewSuggestion,
  useFocusLoadViewSuggestion,
} from 'utils/applyLoadViewSuggestion';
import { applyCompleteKitSuggestions } from 'utils/completeKitSuggestion';
import {
  DEFAULT_MAX_VALIDATION_TOAST_LABELS,
  ManualFieldError,
  applyManualValidationErrors,
  handleFormErrors,
  loadBuildingManualValidationDisplayOptions,
  scheduleLoadBuildingSuggestionValidation,
} from 'utils/formValidation';
import {
  normalizeToStringArray,
  withLoadBuildingSuggestionContext,
} from 'utils/loadInfoAndBuilding';
import { denormalizeDatesForTMSForm } from 'utils/parseDatesForTMSForm';

import {
  validateDeliveryOnOrAfterPickupDate,
  validateDuplicateStopLocations,
} from '../utils/stopValidation';
import { CarrierForm } from './Carrier';
import { CustomerSectionForm } from './Customer';
import { RatesForm } from './Rates';
import { SpecificationsForm } from './Specifications';
import { StopForm } from './Stop';
import { normalizeStarkTransportType } from './utils';

enum AvailableTabs {
  customer = 'customer',
  specifications = 'specifications',
  rates = 'rates',
  pickup = 'pickup',
  consignee = 'consignee',
  carrier = 'carrier',
}

const initLoadBuildingForm = (tmsName: string): NormalizedLoad =>
  normalizeLoad(tmsName, {
    ...createInitLoad(),
    mode: 'TL', // Required as field is read-only
    specifications: {
      ...createInitSpecs(),
      transportType: 'Van',
      serviceType: 'Any',
    },
    rateData: {
      ...createInitRateData(),
      customerTotalCharge: {
        val: 0,
        unit: Unit.USD,
      },
      carrierTotalCost: {
        val: 0,
        unit: Unit.USD,
      },
    },
  });

interface UnifiedStarkLoadFormProps {
  isCreateMode: boolean;
  load?: Load;
  onSubmit?: (data: NormalizedLoad) => void;
  onSuccess?: (result: { load?: Load; loadAttributes?: any }) => void;
  onError?: (error: { load?: Load; loadAttributes?: any }) => void;
  onInvalid?: SubmitErrorHandler<NormalizedLoad>;
  externalLinks?: ExternalLinks;
}

export default function UnifiedStarkLoadForm({
  isCreateMode,
  load,
  onSubmit,
  onSuccess,
  onError,
  onInvalid,
  externalLinks: _externalLinks,
}: UnifiedStarkLoadFormProps) {
  const tmsName = TMS.Stark;
  const { serviceID } = useServiceFeatures();
  const { tmsID } = useTMSContext();
  const { toast } = useToast();
  const guardedCreateLoad = useGuardedCreateLoad();
  const [isLoading, setIsLoading] = useState(false);
  const allTabs = useMemo(() => Object.values(AvailableTabs), []);
  const {
    activeTabs,
    setActiveTabs,
    expandAll,
    collapseAll,
    defaultView,
    setDefaultView,
  } = useLoadBuildingAccordionTabs({ allTabs });
  const [isLoadBuildingSuggestionClicked, setIsLoadBuildingSuggestionClicked] =
    useState(false);
  const [showAIHints, setShowAIHints] = useState(false);

  const [builtLoad, setBuiltLoad] = useState<Maybe<Load>>(null);
  const threadSwitchRestoredForThreadIdRef = useRef<Undef<string>>(undefined);
  const lastFingerprintThreadIdRef = useRef<Undef<string>>(undefined);
  const restoredDraftSuggestionRef = useRef<Maybe<GenericSuggestion>>(null);
  const sawClearedSuggestionAfterRestoreRef = useRef(false);

  const [truckNumbers, setTruckNumbers] = useState<TMSTruckNumber[]>([]);
  const [trailerNumbers, setTrailerNumbers] = useState<TMSTrailerNumber[]>([]);

  const {
    currentState: {
      clickedSuggestion,
      threadId,
      emailId,
      threadItemId,
      goToSuggestion,
    },
    setCurrentState,
  } = useContext(SidebarStateContext);

  if (
    isCreateMode &&
    threadId &&
    lastFingerprintThreadIdRef.current !== threadId
  ) {
    const previousThreadId = lastFingerprintThreadIdRef.current;
    lastFingerprintThreadIdRef.current = threadId;
    if (previousThreadId != null) {
      const savedState = FormStorageService.getFormState<
        NormalizedLoad,
        NormalizedLoad
      >(`${tmsName}_${threadId}`);
      threadSwitchRestoredForThreadIdRef.current =
        savedState?.threadID === threadId && savedState.values
          ? threadId
          : undefined;
    }
  }

  useEffect(() => {
    // Only auto-navigate to suggestions in create mode. Skip when thread-switch
    // already restored (or will restore) a draft for this thread.
    if (
      isCreateMode &&
      !clickedSuggestion &&
      threadSwitchRestoredForThreadIdRef.current !== threadId
    ) {
      goToSuggestion({
        suggestionPipeline: SuggestionPipelines.LoadBuilding,
      });
    }
  }, [isCreateMode, clickedSuggestion, goToSuggestion, threadId]);

  useEffect(() => {
    if (!isCreateMode) {
      return;
    }

    if (threadSwitchRestoredForThreadIdRef.current === threadId) {
      if (!clickedSuggestion) {
        sawClearedSuggestionAfterRestoreRef.current = true;
        return;
      }
      if (!sawClearedSuggestionAfterRestoreRef.current) {
        // Stale previous-thread suggestion before clear flushes.
        return;
      }

      threadSwitchRestoredForThreadIdRef.current = undefined;
      restoredDraftSuggestionRef.current = null;
      sawClearedSuggestionAfterRestoreRef.current = false;
      setIsLoadBuildingSuggestionClicked(true);
      setShowAIHints(true);
      return;
    }

    if (
      clickedSuggestion &&
      clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding
    ) {
      restoredDraftSuggestionRef.current = null;
      setIsLoadBuildingSuggestionClicked(true);
      setShowAIHints(true);
    } else {
      setIsLoadBuildingSuggestionClicked(false);
      setShowAIHints(false);
    }
  }, [isCreateMode, clickedSuggestion, threadId]);

  // Only clear the "Load Created" banner when switching suggestions;
  // keep it when the same suggestion is marked Accepted after building
  useEffect(() => {
    setBuiltLoad(null);
  }, [clickedSuggestion?.id]);

  useEffect(() => {
    // Handle form state restoration from storage (only for create mode).
    // Skip when thread-switch already restored this thread's draft — re-selecting
    // the suggestion would reset defaults over those values.
    if (
      isCreateMode &&
      threadSwitchRestoredForThreadIdRef.current !== threadId
    ) {
      const savedState = FormStorageService.getFormState<
        NormalizedLoad,
        NormalizedLoad
      >(`${tmsName}_${threadId}`);
      if (
        savedState &&
        savedState.threadID === threadId &&
        clickedSuggestion == null
      ) {
        setShowAIHints(true);
        goToSuggestion({
          suggestionID: savedState.clickedSuggestion?.id,
        });
      }
    }
  }, [isCreateMode, threadId, tmsName, clickedSuggestion, goToSuggestion]);

  const createModeSuggestionId = isCreateMode
    ? (clickedSuggestion?.id ?? null)
    : null;

  // Read the suggestion payload through a ref so the defaults below are only
  // re-derived when the create-mode suggestion id changes, never on the
  // transient clickedSuggestion identity churn from extension messages.
  const clickedSuggestionRef = useRef(clickedSuggestion);
  clickedSuggestionRef.current = clickedSuggestion;

  const memoizedDefaultValues: NormalizedLoad = useMemo(() => {
    let base: NormalizedLoad;

    const activeSuggestion =
      createModeSuggestionId == null ? null : clickedSuggestionRef.current;

    // Handle AI suggestions for create mode
    if (builtLoad) {
      base = normalizeLoad(tmsName, builtLoad);
    } else if (
      isCreateMode &&
      activeSuggestion &&
      activeSuggestion.pipeline === SuggestionPipelines.LoadBuilding
    ) {
      const castedClickedSuggestion =
        activeSuggestion.suggested as SuggestedLoad;
      // Email named no equipment: leave it empty for the rep to pick.
      const normalizedTransportType = normalizeStarkTransportType(
        suggestedTransportTypeOrEmpty(castedClickedSuggestion.specifications)
      );

      const suggestedFields = {
        ...castedClickedSuggestion,
        mode: 'TL',
        // Preserve refNumberCandidates fields
        customer: {
          ...castedClickedSuggestion.customer,
          refNumberCandidates: normalizeToStringArray(
            castedClickedSuggestion.customer?.refNumberCandidates
          ),
        },
        pickup: {
          ...castedClickedSuggestion.pickup,
          refNumberCandidates: normalizeToStringArray(
            castedClickedSuggestion.pickup?.refNumberCandidates
          ),
        },
        consignee: {
          ...castedClickedSuggestion.consignee,
          refNumberCandidates: normalizeToStringArray(
            castedClickedSuggestion.consignee?.refNumberCandidates
          ),
        },
        specifications: {
          ...createInitSpecs(),
          ...castedClickedSuggestion.specifications,
          transportType: normalizedTransportType,
        },
        rateData: {
          ...createInitRateData(),
          ...castedClickedSuggestion.rateData,
          customerLineHaulCharge: {
            ...castedClickedSuggestion.rateData?.customerLineHaulCharge,
            unit: Unit.USD,
          },
          customerTotalCharge: {
            ...castedClickedSuggestion.rateData?.customerTotalCharge,
            unit: Unit.USD,
          },
          carrierLineHaulCharge: {
            ...castedClickedSuggestion.rateData?.carrierLineHaulCharge,
            unit: Unit.USD,
          },
          carrierTotalCost: {
            ...castedClickedSuggestion.rateData?.carrierTotalCost,
            unit: Unit.USD,
          },
        },
      };
      setShowAIHints(true);

      base = {
        ...normalizeLoad(tmsName, createInitLoad()),
        ...suggestedFields,
        mode: 'TL',
        rateData: {
          ...suggestedFields.rateData,
          customerTotalCharge: {
            ...suggestedFields.rateData?.customerTotalCharge,
            unit: Unit.USD,
          },
          carrierTotalCost: {
            ...suggestedFields.rateData?.carrierTotalCost,
            unit: Unit.USD,
          },
        },
      } as NormalizedLoad;
    } else if (!isCreateMode && load) {
      // Handle edit mode with existing load data
      base = normalizeLoad(tmsName, load);
    } else {
      base = initLoadBuildingForm(tmsName);
    }

    return base;
  }, [createModeSuggestionId, builtLoad, isCreateMode, load, tmsName]);

  const formMethods = useForm<NormalizedLoad>({
    defaultValues: memoizedDefaultValues,
  });

  function handleClearForm() {
    setShowAIHints(false);
    setBuiltLoad(null);
    setTruckNumbers([]);
    setTrailerNumbers([]);
    setIsLoadBuildingSuggestionClicked(false);
    restoredDraftSuggestionRef.current = null;
    threadSwitchRestoredForThreadIdRef.current = undefined;
    sawClearedSuggestionAfterRestoreRef.current = false;
    FormStorageService.clearFormState(`${tmsName}_${threadId}`);
    setCurrentState((prevState) => ({
      ...prevState,
      clickedSuggestion: null,
    }));
    formMethods.reset(initLoadBuildingForm(tmsName), {
      keepDefaultValues: false,
      keepDirty: false,
      keepDirtyValues: false,
      keepTouched: false,
    });
  }

  // Persist form state on every change (only for create mode)
  const skipFormPersistRef = useRef(false);
  useEffect(() => {
    if (isCreateMode) {
      const subscription = formMethods.watch((value) => {
        if (skipFormPersistRef.current) {
          return;
        }
        FormStorageService.saveFormState(`${tmsName}_${threadId}`, {
          threadID: threadId,
          values: value,
          clickedSuggestion:
            (isLoadBuildingSuggestionClicked && clickedSuggestion) ||
            restoredDraftSuggestionRef.current,
          dirtyFields: formMethods.formState.dirtyFields,
        });
      });
      return () => subscription.unsubscribe();
    }
    return undefined;
  }, [
    isCreateMode,
    formMethods,
    threadId,
    tmsName,
    isLoadBuildingSuggestionClicked,
    clickedSuggestion,
  ]);

  // Create-mode-only Load Building suggestion id. Edit-mode Load View suggestion
  // clicks must not retrigger the reset below, or in-progress edits get wiped.
  const createModeLoadBuildingSuggestionId =
    isCreateMode &&
    clickedSuggestion?.pipeline === SuggestionPipelines.LoadBuilding
      ? clickedSuggestion.id
      : null;

  const suppressDefaultsResetRef = useRef(false);

  // Reset form when default values change
  useEffect(() => {
    if (!memoizedDefaultValues) {
      return;
    }
    if (suppressDefaultsResetRef.current) {
      return;
    }
    formMethods.reset(memoizedDefaultValues, {
      keepDefaultValues: false,
      keepTouched: false,
      keepDirtyValues: false,
      keepDirty: false,
    });

    return scheduleLoadBuildingSuggestionValidation(
      createModeLoadBuildingSuggestionId != null && !builtLoad,
      () => {
        void formMethods.trigger();
      }
    );
  }, [
    memoizedDefaultValues,
    createModeLoadBuildingSuggestionId,
    builtLoad,
    formMethods,
  ]);

  // On email thread switch: drop the previous suggestion and restore this
  // thread's saved draft. Do not re-select the saved suggestion — that would
  // reset defaults over the draft once suppress clears.
  const prevThreadIdRef = useRef<Undef<string>>(undefined);
  const threadSwitchTimeoutRef =
    useRef<Undef<ReturnType<typeof setTimeout>>>(undefined);
  const resetFormOnThreadSwitchRef = useRef<() => void>(() => {});
  resetFormOnThreadSwitchRef.current = () => {
    if (!isCreateMode || !threadId) {
      return;
    }

    if (threadSwitchTimeoutRef.current != null) {
      clearTimeout(threadSwitchTimeoutRef.current);
      threadSwitchTimeoutRef.current = undefined;
    }

    skipFormPersistRef.current = true;
    suppressDefaultsResetRef.current = true;

    setBuiltLoad(null);
    setTruckNumbers([]);
    setTrailerNumbers([]);
    setIsLoadBuildingSuggestionClicked(false);
    setCurrentState((prevState) => ({
      ...prevState,
      clickedSuggestion: null,
    }));

    const savedState = FormStorageService.getFormState<
      NormalizedLoad,
      NormalizedLoad
    >(`${tmsName}_${threadId}`);

    if (savedState && savedState.threadID === threadId && savedState.values) {
      threadSwitchRestoredForThreadIdRef.current = threadId;
      restoredDraftSuggestionRef.current =
        (savedState.clickedSuggestion as Maybe<GenericSuggestion>) ?? null;
      sawClearedSuggestionAfterRestoreRef.current = false;
      formMethods.reset(savedState.values as NormalizedLoad, {
        keepDefaultValues: false,
        keepTouched: false,
        keepDirtyValues: false,
        keepDirty: false,
      });
      const hadSuggestion = savedState.clickedSuggestion?.id != null;
      setIsLoadBuildingSuggestionClicked(hadSuggestion);
      setShowAIHints(hadSuggestion);
    } else {
      threadSwitchRestoredForThreadIdRef.current = undefined;
      restoredDraftSuggestionRef.current = null;
      sawClearedSuggestionAfterRestoreRef.current = false;
      setIsLoadBuildingSuggestionClicked(false);
      setShowAIHints(false);
      formMethods.reset(initLoadBuildingForm(tmsName), {
        keepDefaultValues: false,
        keepTouched: false,
        keepDirtyValues: false,
        keepDirty: false,
      });
    }

    threadSwitchTimeoutRef.current = setTimeout(() => {
      skipFormPersistRef.current = false;
      suppressDefaultsResetRef.current = false;
      threadSwitchTimeoutRef.current = undefined;
    }, 0);
  };

  useEffect(() => {
    if (!threadId) {
      return;
    }
    const previousThreadId = prevThreadIdRef.current;
    prevThreadIdRef.current = threadId;

    if (previousThreadId == null || previousThreadId === threadId) {
      return;
    }
    resetFormOnThreadSwitchRef.current();
  }, [threadId]);

  useEffect(() => {
    return () => {
      if (threadSwitchTimeoutRef.current != null) {
        clearTimeout(threadSwitchTimeoutRef.current);
      }
    };
  }, []);

  useApplyLoadViewSuggestion(
    formMethods.setValue,
    clickedSuggestion,
    load?.freightTrackingID,
    !isCreateMode,
    memoizedDefaultValues
  );

  useFocusLoadViewSuggestion(
    clickedSuggestion,
    load?.freightTrackingID,
    setActiveTabs,
    !isCreateMode,
    Object.values(AvailableTabs)
  );

  const watchedCarrierID = formMethods.watch('carrier.externalTMSID');

  useEffect(() => {
    setTruckNumbers([]);
    setTrailerNumbers([]);

    if (!watchedCarrierID || !tmsID) {
      return;
    }

    const fetchTruckTrailerNumbers = async () => {
      const [truckRes, trailerRes] = await Promise.all([
        getTruckNumbers(tmsID, watchedCarrierID),
        getTrailerNumbers(tmsID, watchedCarrierID),
      ]);

      if (truckRes.isOk()) {
        setTruckNumbers(truckRes.value);
      }
      if (trailerRes.isOk()) {
        setTrailerNumbers(trailerRes.value);
      }
    };

    fetchTruckTrailerNumbers();
  }, [watchedCarrierID, tmsID]);

  const handleSubmit: SubmitHandler<NormalizedLoad> = async (data) => {
    setIsLoading(true);

    const applySubmitValidationErrors = (
      fieldErrors: ManualFieldError<NormalizedLoad>[]
    ) =>
      applyManualValidationErrors(
        formMethods,
        fieldErrors,
        loadBuildingManualValidationDisplayOptions({
          tmsName: TMS.Stark,
          activeTabs,
          setActiveTabs,
          toast,
          afterErrorHandled: () => setIsLoading(false),
        })
      );

    formMethods.clearErrors();

    const duplicateStops = validateDuplicateStopLocations(
      data.pickup?.externalTMSID,
      data.consignee?.externalTMSID
    );
    if (!duplicateStops.ok) {
      applySubmitValidationErrors([
        { path: 'pickup.externalTMSID', message: duplicateStops.message },
        { path: 'consignee.externalTMSID', message: duplicateStops.message },
      ]);
      return;
    }

    const pickupDeliveryDates = validateDeliveryOnOrAfterPickupDate(
      data.pickup?.readyTime,
      data.consignee?.mustDeliver
    );
    if (!pickupDeliveryDates.ok) {
      applySubmitValidationErrors([
        { path: 'consignee.mustDeliver', message: pickupDeliveryDates.message },
      ]);
      return;
    }

    try {
      if (isCreateMode) {
        // Create new load
        data.freightTrackingID = '';
        data.externalTMSID = '';
        const reqData = withLoadBuildingSuggestionContext(
          {
            clickedSuggestion,
            threadId,
            emailId,
            emailExternalId: threadItemId,
          },
          {
            load: {
              ...data,
              tmsID: tmsID,
            } as unknown as Load,
          }
        );

        const res = await guardedCreateLoad(data.customer, reqData);
        if (!res) {
          setIsLoading(false);
          return;
        }
        if (res.isOk()) {
          setBuiltLoad(res.value.load);
          FormStorageService.clearFormState(`${tmsName}_${threadId}`);
          if (
            clickedSuggestion &&
            clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding
          ) {
            setCurrentState((prevState) => ({
              ...prevState,
              ...applyCompleteKitSuggestions(
                prevState,
                clickedSuggestion.id,
                SuggestionPipelines.LoadBuilding
              ),
            }));
          }
          if (onSuccess) {
            onSuccess({ load: res.value.load });
          }
        } else {
          toast({
            description: res.error.message || 'Error building load.',
            variant: 'destructive',
          });
          if (onError) {
            onError({ load: res.error.load });
          }
        }
      } else {
        // Edit mode - update existing load
        const denormalizedData = denormalizeDatesForTMSForm(tmsName, data);

        if (!load?.ID) {
          toast({
            description: 'Load ID is required for updates.',
            variant: 'destructive',
          });
          setIsLoading(false);
          return;
        }

        const res = await updateTMS(load.ID, {
          operation: TMSOperation.UpdateLoad,
          load: denormalizedData as Load,
        });

        if (res.isOk()) {
          toast({
            description: 'Load updated successfully!',
            variant: 'success',
          });
          if (onSuccess) {
            onSuccess({
              load: res.value.load,
              loadAttributes: res.value.loadAttributes,
            });
          }
          await acceptLoadViewSuggestion(
            clickedSuggestion,
            denormalizedData as Load,
            setCurrentState,
            undefined,
            tmsName
          );
        } else {
          toast({
            description: res.error.message || 'Error updating load.',
            variant: 'destructive',
          });
          if (onError) {
            onError({
              load: res.error.load,
              loadAttributes: res.error.loadAttributes,
            });
          }
        }
      }
    } catch {
      toast({
        description: isCreateMode
          ? 'Error building load.'
          : 'Error updating load.',
        variant: 'destructive',
      });
      if (onError) {
        onError({});
      }
    }

    setIsLoading(false);

    // Call custom submit handler if provided
    if (onSubmit) {
      onSubmit(data);
    }
  };

  const handleInvalid: SubmitErrorHandler<NormalizedLoad> = async (errors) => {
    handleFormErrors(errors, {
      tmsName: TMS.Stark,
      maxLabelsToShow: DEFAULT_MAX_VALIDATION_TOAST_LABELS,
      activeTabs,
      setActiveTabs,
      toast,
    });

    if (onInvalid) {
      onInvalid(errors);
    }
  };

  const buttonText = isCreateMode ? ButtonText.BuildLoad : ButtonText.UpdateTMS;

  return (
    <div className='mb-5'>
      <LoadContextProvider
        disabled={true}
        fieldAttributes={[]}
        loadAttrsObj={{} as any}
        invalidateLoad={() => Promise.resolve()}
      >
        <ExtendedFormProvider
          aiDefaultValues={showAIHints}
          highlightDirtyFields={!isCreateMode}
        >
          <FormProvider {...formMethods}>
            <form
              onSubmit={formMethods.handleSubmit(handleSubmit, handleInvalid)}
            >
              <LoadBuildingAccordionControls
                activeTabs={activeTabs}
                allTabs={allTabs}
                onExpandAll={expandAll}
                onCollapseAll={collapseAll}
                defaultView={defaultView}
                onDefaultViewChange={setDefaultView}
              />
              <Accordion
                type='multiple'
                value={activeTabs}
                onValueChange={setActiveTabs}
              >
                <LoadSectionAccordionItem
                  label='Customer'
                  icon={<Building2 className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.customer}
                  activeTabs={activeTabs}
                >
                  <CustomerSectionForm
                    formMethods={formMethods}
                    originalSuggestionData={undefined}
                    isCreateMode={isCreateMode}
                  />
                </LoadSectionAccordionItem>

                <LoadSectionAccordionItem
                  label='Specs'
                  icon={<Weight className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.specifications}
                  activeTabs={activeTabs}
                >
                  <SpecificationsForm
                    formMethods={formMethods}
                    isCreateMode={isCreateMode}
                  />
                </LoadSectionAccordionItem>

                <LoadSectionAccordionItem
                  label='Rates'
                  icon={
                    <CircleDollarSignIcon className='h-6 w-6' strokeWidth={1} />
                  }
                  name={AvailableTabs.rates}
                  activeTabs={activeTabs}
                >
                  <RatesForm
                    formMethods={formMethods}
                    load={memoizedDefaultValues}
                  />
                </LoadSectionAccordionItem>

                <LoadSectionAccordionItem
                  label='Pickup'
                  icon={<WarehouseIcon className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.pickup}
                  activeTabs={activeTabs}
                >
                  <StopForm
                    formMethods={formMethods}
                    stopType='pickup'
                    load={memoizedDefaultValues}
                    isCreateMode={isCreateMode}
                  />
                </LoadSectionAccordionItem>

                <LoadSectionAccordionItem
                  label='Consignee'
                  icon={<BoxIcon className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.consignee}
                  activeTabs={activeTabs}
                >
                  <StopForm
                    formMethods={formMethods}
                    stopType='consignee'
                    load={memoizedDefaultValues}
                    isCreateMode={isCreateMode}
                  />
                </LoadSectionAccordionItem>
                <LoadSectionAccordionItem
                  label='Carrier'
                  icon={<TruckIcon className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.carrier}
                  activeTabs={activeTabs}
                >
                  <CarrierForm
                    formMethods={formMethods}
                    truckNumbers={truckNumbers}
                    trailerNumbers={trailerNumbers}
                  />
                </LoadSectionAccordionItem>
              </Accordion>

              <section className='w-full mt-4'>
                <Button
                  buttonNamePosthog={
                    isCreateMode
                      ? ButtonNamePosthog.BuildLoad
                      : ButtonNamePosthog.UpdateTMS
                  }
                  type='submit'
                  className='w-full'
                  disabled={isLoading}
                  data-testid={
                    isCreateMode
                      ? 'create-load-submit'
                      : 'load-info-update-submit'
                  }
                  logProperties={{ serviceID }}
                >
                  {isLoading ? <ButtonLoader /> : buttonText}
                </Button>

                {isCreateMode && formMethods.formState.isDirty && (
                  <Flex justify='center' align='center'>
                    <Button
                      buttonNamePosthog={ButtonNamePosthog.ClearForm}
                      type='button'
                      className='w-50% mt-4 h-8 text-sm text-neutral-500'
                      disabled={isLoading}
                      variant='outline'
                      onClick={handleClearForm}
                      logProperties={{ serviceID }}
                    >
                      {ButtonText.ClearForm}
                    </Button>
                  </Flex>
                )}

                {isCreateMode && builtLoad?.externalTMSID && (
                  <div className='whitespace-pre-wrap my-3 rounded py-3 text-neutral-800 px-4 bg-success-50'>
                    <Typography className='mb-2'>Load Created 🎉</Typography>
                    <Typography variant='body-sm' className='mb-2'>
                      <Typography variant='body-sm' weight='bold'>
                        Stark Shipment ID:{' '}
                      </Typography>
                      {builtLoad.externalTMSID}
                    </Typography>
                    {builtLoad.freightTrackingID && (
                      <Typography variant='body-sm' className='mb-2'>
                        <Typography variant='body-sm' weight='bold'>
                          Freight Tracking ID:{' '}
                        </Typography>
                        {builtLoad.freightTrackingID}
                      </Typography>
                    )}
                  </div>
                )}
              </section>
            </form>
          </FormProvider>
        </ExtendedFormProvider>
      </LoadContextProvider>
    </div>
  );
}
